import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { updateEditableMedia, EditableMediaData } from '../../../../containers/Editor/actions';
import { PositionAndSize, getNumberFromString } from '../../../../containers/Editor/utils/helpers';
import MediaRnd from '../MediaRnd';
import { EditableType, EditorType } from '../../../../containers/Editor/constants';
import Utils from '../../../../utils';

interface MediaItemProps {
	editorType: EditorType;
	type: EditableType;
	src: string;
	scale: number;
	isEditedFromBackoffice: boolean;
	isActive: boolean;
	isLoaded: boolean;
	onAfterLoad: () => void;
	page: number;
	id: string;
	frameWidth: number;
	frameHeight: number;
	width: number;
	height: number;
	x: number;
	y: number;
	canDragAndResize: boolean;
	isKeyActionsDisabled: boolean;
	dispatch: Dispatch<StoreState>;
	isCustom?: boolean;
	isDefaultMedia?: boolean;
	parentContainer?: Element | null;
	iframeContainer?: HTMLIFrameElement | null;
	frameAspectRatio?: number;
	onLoad?: (width: number, height: number) => void;
	onClick?: () => void;
	onClickOutside?: (positionAndSize: PositionAndSize) => void;
}

const MediaItem = ({
	editorType,
	type,
	src,
	scale,
	isEditedFromBackoffice,
	isActive,
	isLoaded,
	isDefaultMedia,
	onAfterLoad,
	page,
	id,
	frameWidth,
	frameHeight,
	width,
	height,
	x,
	y,
	canDragAndResize,
	isKeyActionsDisabled,
	dispatch,
	isCustom,
	parentContainer,
	iframeContainer,
	frameAspectRatio,
	onLoad,
	onClick,
	onClickOutside,
}: MediaItemProps) => {
	const imgElement = React.useRef<HTMLImageElement>(null);
	const [isError, setIsError] = React.useState(false);

	const handleLoad = () => {
		setIsError(false);

		if (isLoaded) {
			return;
		}

		if (isCustom && onLoad) {
			const { naturalWidth, naturalHeight } = imgElement.current as HTMLImageElement;
			onLoad(naturalWidth, naturalHeight);
		} else {
			dispatch(updateEditableMedia(page, id, {
				isLoaded: true,
				isEditedFromBackoffice,
			}, false));
		}

		onAfterLoad();
	};

	const handleClick = () => {
		if (editorType !== EditorType.HTML || !parentContainer) {
			return;
		}

		const {
			width: frameWidth,
			height: frameHeight,
		} = parentContainer.getBoundingClientRect();
		const {
			naturalWidth,
			naturalHeight,
			style: { width, top, left },
		} = imgElement.current as HTMLImageElement;
		const imageAspectRatio = naturalHeight / naturalWidth;
		const data: EditableMediaData = {
			frameWidth,
			frameHeight,
			width: getNumberFromString(width as string),
			height: ((frameWidth * getNumberFromString(width as string)) * imageAspectRatio) / frameHeight,
			x: getNumberFromString(left as string),
			y: getNumberFromString(top as string),
			isEditedFromBackoffice,
		};

		if (isDefaultMedia) {
			// keep aspect ratio from default media to calculate height of frame in <MediaEditor>
			// and use it on Reader side too (via data-uprez-aspect-ratio attribute)
			data.frameAspectRatio = imageAspectRatio;
		}

		dispatch(updateEditableMedia(page, id, data, false));

		if (onClick) {
			onClick();
		}
	};

	// set frame sizes for custom media on component mount
	// it can be done only when parentContainer is rendered
	// IntersectionObserver is used because parentContainer can be hidden on mount (e.g., in slider) so its width equals 0 which causes incorrect calculation
	React.useEffect(() => {
		if (editorType !== EditorType.HTML || !parentContainer || isDefaultMedia) {
			return;
		}

		const handleIntersect = (entries: IntersectionObserverEntry[]) => {
			entries.forEach(({ isIntersecting }) => {
				if (isIntersecting) {
					const { width: frameWidth } = parentContainer.getBoundingClientRect();

					dispatch(updateEditableMedia(page, id, {
						frameWidth,
						frameHeight: frameWidth * (frameAspectRatio as number),
					}, false));
				}
			});
		};

		const observer = new IntersectionObserver(handleIntersect);
		observer.observe(parentContainer);

		return () => {
			observer.unobserve(parentContainer);
		};
	}, []);

	const defaultImage = type === EditableType.image
		? 'iconset_uprez_image'
		: 'iconset_uprez_play-button';

	const hasImageStyle = editorType === EditorType.HTML &&
		type === EditableType.image &&
		!isActive &&
		!isDefaultMedia;

	const imageStyle: React.CSSProperties | undefined = hasImageStyle
		? {
			position: 'absolute',
			width: `${width}%`,
			height: 'auto',
			top: `${y}%`,
			left: `${x}%`,
		}
		: undefined;

	const Image = (
		<>
			<img
				// prop key is used to remove previous loaded image in order to display new image uploading
				key={src}
				ref={imgElement}
				src={src}
				alt=""
				hidden={isError}
				onLoad={handleLoad}
				onError={() => setIsError(true)}
				onDragStart={e => e.preventDefault()}
				onClick={handleClick}
				style={imageStyle}
			/>
			{isError && Utils.getSvgIcon(defaultImage, { onClick: handleClick })}
		</>
	);

	if (canDragAndResize) {
		return (
			<MediaRnd
				id={id}
				page={page}
				frameWidth={frameWidth}
				frameHeight={frameHeight}
				width={width}
				height={height}
				x={x}
				y={y}
				scale={scale}
				isActive={isActive}
				isLoaded={isLoaded}
				isKeyActionsDisabled={isKeyActionsDisabled}
				dispatch={dispatch}
				isCustom={isCustom}
				onClickOutside={onClickOutside}
				iframeContainer={iframeContainer}
				isPercentageValues={editorType === EditorType.HTML}
			>
				{Image}
			</MediaRnd>
		);
	}

	return Image;
};

export default MediaItem;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/MediaEditor/MediaItem/index.tsx